import * as flatbuffers from 'flatbuffers';
import { inject, injectable } from 'inversify';
import type { CaseInfo } from './CaseInfoApi';
import type { NewPackageFileCreateInfo, NewPackageFileUploadInfo, NewPackageInfo } from './CaseFilesApi';
import { MCSData } from '../Serialization/MCSD/m-c-s-data';
import { TYPES } from '../ioc';

export interface BlobInfo {
  descriptor: string;
  url: string;
  md5?: string;
  expiration: number;
}

export interface PackageInfo {
  blobs: Map<string, BlobInfo>;
  packageId: string;
}

export interface CaseBlob {
  descriptor: string;
  fileName?: string;
  blob: Blob;
}

export interface ICaseDataRepository {
  setContext: (caseId: string, packageId: string) => void;
  getCaseInfo: () => Promise<CaseInfo>;
  getCaseFilesList: () => Promise<Array<BlobInfo>>;
  getCaseFileInfo: (fileDescriptor: string) => Promise<BlobInfo>;
  getCaseBlobUrlResolver: () => (descriptor: string) => Promise<string>;
  hasCaseBlobInfo: (descriptor: string) => Promise<boolean>;
  getCaseBlob: (descriptor: string) => Promise<ArrayBuffer>;
  uploadBlob: (blobUploadUrl: string, blob: Blob) => Promise<Response>;
  derivePackage: (files: Array<NewPackageFileCreateInfo>) => Promise<NewPackageInfo>;
  saveDerivedPackage: (newPackageId: string) => Promise<void>;
  approveDerivedPackage: (newPackageId: string) => Promise<string>;
  rejectDerivedPackage: (newPackageId: string) => Promise<string>;
}
export type RepositoryType = 'network' | 'offline' | 'auto';

export interface ICaseDataRepositoryFactory {
  create: (repositoryType?: RepositoryType) => ICaseDataRepository;
}

@injectable()
export class CaseDataProvider {
  private _caseId?: string;

  private _packageId?: string;

  private _caseDataRepository: ICaseDataRepository;

  private _caseInfoInitPromise?: Promise<CaseInfo>;

  private _packageInfoInitPromise?: Promise<Array<BlobInfo>>;

  public constructor(@inject(TYPES.ICaseDataRepositoryFactory) caseDataRepositoryFactory: ICaseDataRepositoryFactory) {
    this._caseDataRepository = caseDataRepositoryFactory.create();
  }

  private _initCaseInfo(): Promise<CaseInfo> {
    if (this._caseInfoInitPromise) {
      console.warn('Case Info is already initialized');
    } else {
      this._caseInfoInitPromise = this._caseDataRepository.getCaseInfo();
    }
    return this._caseInfoInitPromise;
  }

  private _initPackageInfo(): Promise<Array<BlobInfo>> {
    // [chebotarev]
    // Herein we are retrieving and caching all the STS package file's links.
    // This is performance-optimized single server API call.
    // Retrieval of the links one by one is mush less performant.
    if (this._packageInfoInitPromise) {
      throw new Error('Package Info is already initialized');
    }
    this._packageInfoInitPromise = this._caseDataRepository.getCaseFilesList();
    return this._packageInfoInitPromise;
  }

  public async init(caseId: string, packageId: string): Promise<void> {
    this._caseId = caseId;
    this._packageId = packageId;
    this._caseDataRepository.setContext(caseId, packageId);
    return (await Promise.all([this._initCaseInfo(), this._initPackageInfo()])) as unknown as Promise<void>;
  }

  public async getCaseInfo(): Promise<CaseInfo> {
    if (!this._caseInfoInitPromise) {
      throw new Error('Case Info is not initialized');
    }
    return this._caseInfoInitPromise;
  }

  public getShortLivingCaseBlobUrlResolver(): (descriptor: string) => Promise<string> {
    return this._caseDataRepository.getCaseBlobUrlResolver();
  }

  public async hasCaseBlob(descriptor: string): Promise<boolean> {
    if (!this._packageInfoInitPromise) {
      throw new Error('Package Info is not initialized');
    }
    return this._caseDataRepository.hasCaseBlobInfo(descriptor);
  }

  public async getCaseBlob(descriptor: string): Promise<ArrayBuffer> {
    return this._caseDataRepository.getCaseBlob(descriptor);
  }

  public async getCaseHeader(): Promise<MCSData> {
    const buffer = await this.getCaseBlob('CaseHeader.mcsd');
    const data = new Uint8Array(buffer);
    const buf = new flatbuffers.ByteBuffer(data);
    return MCSData.getRootAsMCSData(buf);
  }

  public getCaseId(): string {
    if (!this._caseId) {
      throw new Error('Not initialized');
    }
    return this._caseId;
  }

  public getPackageId(): string {
    if (!this._packageId) {
      throw new Error('Not initialized');
    }
    return this._packageId;
  }

  private async _derivePackage(blobs: Array<CaseBlob>): Promise<string> {
    const infos = new Array<NewPackageFileCreateInfo>();
    const blobsMap = new Map<string, Blob>();
    blobs.forEach((entry: CaseBlob): void => {
      infos.push({ descriptor: entry.descriptor, fileName: entry.fileName, md5: undefined });
      blobsMap.set(entry.descriptor, entry.blob);
    });
    const newPackageInfo = await this._caseDataRepository.derivePackage(infos);
    const fetches = new Array<Promise<Response>>();
    newPackageInfo.uploadUrls.forEach((entry: NewPackageFileUploadInfo): void => {
      const blob = blobsMap.get(entry.descriptor);
      if (!blob) {
        throw new Error('New package is incomplete');
      }
      fetches.push(this._caseDataRepository.uploadBlob(entry.url, blob));
    });
    await Promise.all(fetches);
    return newPackageInfo.packageId;
  }

  public async saveCase(blobs: Array<CaseBlob>): Promise<string> {
    if (!this._caseId || !this._packageId) {
      throw new Error('Not initialized');
    }
    const newPackageId = await this._derivePackage(blobs);
    await this._caseDataRepository.saveDerivedPackage(newPackageId);
    this._caseDataRepository.setContext(this._caseId, newPackageId);
    this._packageId = newPackageId;
    return this._packageId;
  }

  public async approveCase(blobs: Array<CaseBlob>): Promise<string> {
    if (!this._caseId || !this._packageId) {
      throw new Error('Not initialized');
    }
    const newPackageId = await this._derivePackage(blobs);
    return await this._caseDataRepository.approveDerivedPackage(newPackageId);
  }

  public async rejectCase(blobs: Array<CaseBlob> = []): Promise<string> {
    if (!this._caseId || !this._packageId) {
      throw new Error('Not initialized');
    }
    const newPackageId = await this._derivePackage(blobs);
    return await this._caseDataRepository.rejectDerivedPackage(newPackageId);
  }
}
